import { ReactNode } from 'react';

export type BadgeVariant =
  | 'draft'
  | 'production'
  | 'delivered'
  | 'approved'
  | 'rejected'
  | 'published'
  | 'paid'
  | 'pending'
  | 'neutral'
  | 'info';

interface BadgeProps {
  children: ReactNode;
  variant?: BadgeVariant;
  size?: 'sm' | 'md';
  className?: string;
}

const variantClasses: Record<BadgeVariant, string> = {
  draft: 'bg-gray-100 text-gray-700 border-gray-200',
  production: 'bg-blue-100 text-blue-700 border-blue-200',
  delivered: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  approved: 'bg-green-100 text-green-700 border-green-200',
  rejected: 'bg-red-100 text-red-700 border-red-200',
  published: 'bg-green-100 text-green-700 border-green-200',
  paid: 'bg-green-100 text-green-700 border-green-200',
  pending: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  neutral: 'bg-gray-100 text-gray-600 border-gray-200',
  info: 'bg-blue-50 text-blue-600 border-blue-100'
};

const sizeClasses = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-1 text-xs'
};

export function Badge({ children, variant = 'neutral', size = 'md', className = '' }: BadgeProps) {
  return (
    <span
      className={`
        inline-flex items-center justify-center
        font-semibold uppercase tracking-wide
        rounded-md border
        ${variantClasses[variant]}
        ${sizeClasses[size]}
        ${className}
      `}
    >
      {children}
    </span>
  );
}

interface CountBadgeProps {
  count: number;
  variant?: 'primary' | 'secondary' | 'success';
  className?: string;
}

const countVariantClasses = {
  primary: 'bg-blue-100 text-blue-700',
  secondary: 'bg-gray-100 text-gray-700',
  success: 'bg-green-100 text-green-700'
};

export function CountBadge({ count, variant = 'primary', className = '' }: CountBadgeProps) {
  return (
    <span
      className={`
        inline-flex items-center justify-center
        min-w-[24px] h-6 px-2
        text-xs font-semibold
        rounded-full
        ${countVariantClasses[variant]}
        ${className}
      `}
    >
      {count}
    </span>
  );
}
